import { useLocation, useNavigate } from "react-router";
import type { ComponentType, SVGProps } from "react";
import type { NavItem } from "@atrium/shared";
import { useMe } from "../features/auth/AuthProvider";
import { useUnread } from "../lib/hooks";
import { usePrefs } from "../lib/prefs";
import { cn } from "../lib/cn";
import { Badge } from "../components/ui/Controls";
import { PresenceAvatar } from "../components/PresenceIndicator";
import { NAV, orderedNav, useNavTarget } from "./Rail";

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

/** Four slots plus your profile. When more entries are visible, these give way first. */
const MOBILE_SLOTS = 4;
const DROP_FIRST: NavItem[] = ["saved", "explore"];

export function mobileNav(items: NavItem[]): NavItem[] {
  const out = [...items];
  for (const d of DROP_FIRST) if (out.length > MOBILE_SLOTS && out.includes(d)) out.splice(out.indexOf(d), 1);
  return out.slice(0, MOBILE_SLOTS);
}

function Tab({ label, icon: I, activeIcon: A, active, onClick, badge }: { label: string; icon: Icon; activeIcon: Icon; active: boolean; onClick: () => void; badge?: number }) {
  const Cmp = active ? A : I;
  return (
    <button onClick={onClick} aria-label={label} aria-current={active ? "page" : undefined} className={cn("relative flex h-14 flex-1 items-center justify-center", active ? "text-fg" : "text-fg-3")}>
      <Cmp className="icon size-6" aria-hidden />
      {!!badge && <Badge count={badge} className="absolute left-1/2 top-2 ml-1.5" />}
    </button>
  );
}

export function MobileTabBar() {
  const me = useMe();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: unread } = useUnread();
  const prefs = usePrefs();
  const target = useNavTarget();
  const profileActive = pathname === `/u/${me.username}`;
  return (
    <nav aria-label="Primary" className="safe-bottom flex shrink-0 border-t border-line bg-bg-subtle/95 backdrop-blur md:hidden">
      {mobileNav(orderedNav(prefs)).map((item) => {
        const def = NAV[item];
        return (
          <Tab
            key={item}
            label={def.label}
            icon={def.icon}
            activeIcon={def.activeIcon}
            active={def.isActive(pathname)}
            onClick={() => navigate(target(item, pathname))}
            badge={item === "messages" ? unread?.conversations : item === "notifications" ? unread?.notifications : undefined}
          />
        );
      })}
      <button onClick={() => navigate(`/u/${me.username}`)} aria-label="Profile" aria-current={profileActive ? "page" : undefined} className="flex h-14 flex-1 items-center justify-center">
        <span className={cn("rounded-full p-0.5 [--presence-ring:var(--bg-subtle)]", profileActive && "ring-2 ring-fg")}>
          <PresenceAvatar userId={me.id} src={me.avatarUrl} name={me.displayName} size="sm" />
        </span>
      </button>
    </nav>
  );
}
